'use client'

import { Modal } from '@/components/client/third-party/radix-ui/modal'
import { useTranslation } from '@/hooks/client/use-translation'
import SvgXmark from '@/icons/regular/Xmark'
import type { FilterOptions } from '@/lib/directory/build-filter-options'
import type { FacetCounts } from '@/lib/directory/facet-counts'

import { FilterGroups } from './filter-groups'
import { SortPills } from './sort-pills'

import { trackDirectory } from '../track-directory'
import { ActionType, type DirectoryAction, type DirectoryFilters, type SortOption } from '../use-directory-reducer'

interface FilterSheetProps {
  isOpen: boolean
  filters: DirectoryFilters
  sort: SortOption
  filterOptions: FilterOptions
  counts: FacetCounts
  dispatch: React.Dispatch<DirectoryAction>
  resultCount: number
}

// Phones and tablets: sort first, then the same facet groups, in a bottom
// sheet with the live result count on the button that closes it.
export const FilterSheet = ({
  isOpen,
  filters,
  sort,
  filterOptions,
  counts,
  dispatch,
  resultCount,
}: FilterSheetProps) => {
  const { t } = useTranslation()
  const close = () => dispatch({ type: ActionType.ToggleModal })
  const title = t('client.directory.filters', 'Filters')

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={title}
      classes={{
        panelContainer: 'items-end sm:items-center',
        panel:
          'flex max-h-[92svh] w-full max-w-none flex-col rounded-t-2xl rounded-b-none sm:max-h-[85svh] sm:max-w-xl sm:rounded-2xl',
      }}
    >
      <div className="border-navy/10 flex min-h-[60px] items-center justify-between border-b pr-3 pl-5">
        <h2 className="text-navy text-[18px] font-semibold">{title}</h2>
        <button
          type="button"
          data-tracking="ignore"
          aria-label={t('directory.filters.close', 'Close filters')}
          onClick={close}
          className="hover:bg-navy/5 text-navy flex h-11 w-11 cursor-pointer items-center justify-center rounded-full"
        >
          <SvgXmark className="h-5 w-5 fill-current" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5">
        <SortPills
          value={sort}
          onChange={(next) => {
            trackDirectory('directory-sort', { sort: next })
            dispatch({ type: ActionType.SetSort, sort: next })
          }}
        />
        <FilterGroups
          filters={filters}
          filterOptions={filterOptions}
          counts={counts}
          dispatch={dispatch}
          idPrefix="sheet"
        />
      </div>
      <div className="border-navy/10 flex items-center justify-between gap-4 border-t px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          data-tracking="ignore"
          onClick={() => dispatch({ type: ActionType.ClearFilters })}
          className="text-navy cursor-pointer text-[15px] font-medium underline decoration-dotted underline-offset-4"
        >
          {t('client.directory.clearAll', 'Clear all')}
        </button>
        <button
          type="button"
          data-tracking="ignore"
          onClick={close}
          className="bg-navy hover:bg-navy/90 min-h-[48px] flex-1 cursor-pointer rounded-xl px-5 text-[16px] font-semibold text-white transition-colors sm:flex-none"
        >
          {t('directory.filters.showFamilies', 'Show {{count}} families', { count: resultCount })}
        </button>
      </div>
    </Modal>
  )
}
